import type { StationRole } from '@/objects/generated';
import type { RaceContestantId, RaceId, StationRoleId } from '@/utilities/typealiases';
import type { MatrixContestant } from './MatrixContestant';
import type { MatrixStationRoleColumnLayout } from './MatrixHelper';
import { arrayGroupbyToMap } from '@/utilities';

type RaceSegment = {
    IsStart: boolean;
    IsPreStart: boolean;
    IsStartControl: boolean;
    IsFinish: boolean;
    IsShooting: boolean;
    ShootingNumber: number;
    StationRoles: StationRole[];
};

// Id to fallback to if a stationrole has NULL as raceid.
const RaceIdNullValue = 0;

/**
 * Builds the column layout for station roles across multiple races.
 *
 * @param raceIds - The list of race IDs to include in the layout.
 * @param stationRoles - A map of station role IDs to station role objects.
 * @param matrixContestants - A map of race contestant IDs to matrix contestant objects.
 * @returns An array of matrix station role column layouts.
 */
export const buildStationRoleColumnLayoutForRaces = (
    raceIds: RaceId[],
    stationRoles: Map<StationRoleId, StationRole>,
    matrixContestants: Map<RaceContestantId, MatrixContestant>,
): MatrixStationRoleColumnLayout[] => {
    const stationRolesForRaces = Array.from(stationRoles.values()).filter((role) => role.RaceID == null || raceIds?.includes(role.RaceID));
    stationRolesForRaces.sort((a, b) => a.Sort - b.Sort);
    const stationRolesPerRace = arrayGroupbyToMap(stationRolesForRaces, (role) => role.RaceID ?? RaceIdNullValue);

    if (stationRolesPerRace.size === 0) {
        return [];
    }

    // If we have shooting columns, they should align so shooting 1 is in the same column for all races.
    // We cant mix a shooting and a non-shooting column since they will render differently, and sort differently and so on.

    // To do this we need to know how many non-shooting roles are between each shooting so we can fit everything.
    // It might be tempting to find the highest index of each shootingnumber for each race, but that doesnt work
    // If  Race A is:    Shooting1, pass1,  shooting2,   pass2,      pass3
    // and Race B is:    pass1,     pass2,  pass3,       shooting1, shooting2
    // We find that shooting1 is column 4, and shooting2 is column 5 (highest index from Race B)
    // If we are then going to place Race A, shooting1 will be shifted to column4 as it should, but then pass1 should be column5, which we have determined is a shooting column. This wont work
    // The correct layout here would be shooting1 in column4 and shooting 2 in column 6 (Race A shooting 1 offset + Race A non-shooting roles between shooting1 and shooting2 + 1 for the shooting2 itself)

    // So the way we are going to do this is to split the race into "segments", where a segment is a set of roles.
    // The segments are separated by segment boundries, which is just a segment with a single role.
    // The segment boundries are the start, start control, shootings, and finish
    // So we represent everything as a RaceSegment, and the boundries are just segments that contain a single role.
    // With segment boundries represented as [boundrytype] we want to make this:
    // prestart, [start] [startcontrol], segment1, [shooting1], segment2, [shooting2], ..., [finish]

    // Within each segment we want to align roles with the same name in the same column.
    // Note that this might not always be possible, if we have reversed names in two different races.
    // Race A: pass1, pass2
    // Race B: pass2, pass1
    // In this case, aligning roles with the same name in the same column is not possible.

    // Array to hold the segments
    const segments: RaceSegment[] = [];

    // Itereate all the roles for each race and find/create the segments they fit into
    stationRolesPerRace.forEach((roles, _raceId) => {
        const startIndex = roles.findIndex((role) => role.Start);

        let currentShootingNumber = 0; // Which shooting segment we are currently in

        // Iterate the roles for one race
        roles.forEach((role, index) => {
            if (role.IsShootingStation) {
                currentShootingNumber++;
            }

            // Find the segment this role belongs to based on its type (start, start control, shooting, finish)
            // If no existing segment fits this role, a new segment will be added by the function.
            const segment = findSegmentThatFitsCurrentRoleOrAddNew(segments, role, index, startIndex, currentShootingNumber);

            segment.StationRoles.push(role);
        });
    });

    // These are all the columns. This will be turned into the actual MatrixColumns later, but we need the full StationRoles for now.
    const columns: { IsShootingColumn: boolean; Roles: StationRole[] }[] = [];

    // Turn our segments into columns.
    // One segment can end up being multiple columns if its not a boundary segment.
    segments.forEach((segment) => {
        // If its a boundry, we can just create one column directly in the main columns array
        if (segment.IsPreStart || segment.IsStart || segment.IsStartControl || segment.IsShooting || segment.IsFinish) {
            columns.push({
                IsShootingColumn: segment.IsShooting,
                Roles: segment.StationRoles,
            });
            return;
        }

        // These are the columns for this segment
        const segmentColumns: { Roles: StationRole[] }[] = [];

        // We have all the stationRoles for this segment here (across races), but they are just in one long array
        // We need to split them into per race
        const rolesByRace = arrayGroupbyToMap(segment.StationRoles, (role) => role.RaceID ?? RaceIdNullValue);

        // We want to do the longest race first, that ensures that the first iteration of the loop seeds the total number of columns.
        const raceIdsSortedBySegmentLength = Array.from(rolesByRace.entries()).sort((a, b) => {
            const compare = b[1].length - a[1].length;
            if (compare === 0) {
                // For same length races, use the ID to sort, so we always have consistent sorting for the same races
                return a[0] - b[0];
            }
            return compare;
        });

        // Go through the races, taking the longest first
        raceIdsSortedBySegmentLength.forEach(([_, rolesForOneRace], raceByLengthIndex) => {
            let previousColumnIndex = -1; // Where we ended up putting the previous column for this race. This number should increase for each role we place.

            // Iterate the roles for this race, trying to place them in the appropriate columns
            rolesForOneRace.forEach((roleWeArePlacingNow, roleWeArePlacingNowIndex) => {
                // We want to place roles with matching names in the same column.
                // This might not always be possible
                // If we have Race A: pass1, pass2
                //            Race B: pass2, pass1
                // Since we have the reverse order, we cant align the columns since they conflict.
                // If we move A's pass1 to the second column to align with B's pass1, we need more columns since A has more after pass1
                // This is checked by seeing if we can move the role to a later column and still have room for the remaining roles in this race.

                // Calculate how many roles are left to place after this one
                const rolesLeftToPlaceAfterThisOne = rolesForOneRace.length - roleWeArePlacingNowIndex - 1;

                // Try to find a column that already has this name, that is early enough that we can fit all the columns for this race
                const targetColumn =
                    segmentColumns.find((column, colIndex) => {
                        // Skip if we dont have name (this spins all the columns, but whatever)
                        if (!roleWeArePlacingNow.Name || !roleWeArePlacingNow.Name.length) {
                            return false;
                        }
                        // If we placed a previous column earlier than the column we are looking at now, we cant choose it
                        if (colIndex <= previousColumnIndex) {
                            return false;
                        }

                        // Calculate how many columns are left after this one
                        const columnsLeftAfterThis = segmentColumns.length - colIndex - 1;

                        // If we have walked so far that we cant fit the remaining roles for this race, we cant find name match
                        if (columnsLeftAfterThis < rolesLeftToPlaceAfterThisOne) {
                            return false;
                        }

                        // Check if name matches
                        return column.Roles.some((sr) => sr.Name == roleWeArePlacingNow.Name);
                    }) ??
                    // If we cant find a match on name, we use the next column after where we placed the previous role
                    segmentColumns.at(Math.max(roleWeArePlacingNowIndex, previousColumnIndex + 1));

                // Place in column, or create a new column if necessary
                if (targetColumn) {
                    targetColumn.Roles.push(roleWeArePlacingNow);
                    previousColumnIndex = segmentColumns.indexOf(targetColumn);
                } else {
                    segmentColumns.push({ Roles: [roleWeArePlacingNow] });
                    previousColumnIndex = segmentColumns.length - 1;

                    // If we are trying to add a new column after we have handled the first race, then something went wrong
                    if (raceByLengthIndex > 0) {
                        throw Error('We should never get here');
                    }
                }
            });
        });

        // After processing all races for this segment, we can push the segmentColumns to the main columns array
        segmentColumns.forEach((column) => {
            columns.push({
                IsShootingColumn: false, // This is always false here since we did the boundry columns already
                Roles: column.Roles,
            });
        });
    });

    // We have now created the layout for one lap. we should multiply the layout for the number of laps each race has

    // Find the maximum number of laps per race
    const maxLapsPerRace = [...matrixContestants.values()]
        .filter((c) => raceIds?.includes(c.RaceID) ?? false)
        .reduce((accumulator, c) => {
            const currentMax = accumulator.get(c.RaceID) ?? 1;
            accumulator.set(c.RaceID, Math.max(currentMax, c.MaxLaps));
            return accumulator;
        }, new Map<RaceId, number>());

    // Find the maximum number of laps across all races
    const maxLaps = Math.max(...maxLapsPerRace.values(), 1);

    const finalColumns: MatrixStationRoleColumnLayout[] = [];
    // We already have the first lap, so start on lap2 and copy the column layout as long as we have more laps
    for (let lap = 1; lap <= maxLaps; lap++) {
        columns.forEach((column) => {
            // Not all races have the same number of laps, so a late column should only have the stationroles that are in races that still have laps remaining
            const rolesForLap = column.Roles.filter((role) => (maxLapsPerRace.get(role.RaceID ?? -1) ?? 1) >= lap);

            // Push in a copy with the correct stationroles
            finalColumns.push({
                IsShootingColumn: column.IsShootingColumn,
                Roles: rolesForLap.map((role) => ({
                    Lap: lap,
                    StationRoleId: role.ID,
                })),
            });
        });
    }
    return finalColumns;
};

/**
 * Finds the segment that fits the current role or adds a new one if it doesn't exist
 *
 * @param segments The list of existing race segments
 * @param role The current station role being placed
 * @param roleIndex The index of the current role in the overall list
 * @param startIndex The index where the start segment begins
 * @param currentShootingNumber The current shooting segment number
 * @returns The segment that fits the current role or a newly created one if it doesn't exist
 */
const findSegmentThatFitsCurrentRoleOrAddNew = (
    segments: RaceSegment[],
    role: StationRole,
    roleIndex: number,
    startIndex: number,
    currentShootingNumber: number,
) => {
    let segment;
    // Find the segment for the pre-start
    if (roleIndex < startIndex) {
        segment = segments.find((segment) => segment.IsPreStart);
    }
    // Find the segment for the start boundry
    if (role.Start) {
        segment = segments.find((segment) => segment.IsStart);
    }
    // Find the segment for the start control boundry
    if (role.StartControl) {
        segment = segments.find((segment) => segment.IsStartControl);
    }
    // Find the segment for the finish boundry
    if (role.Finish) {
        segment = segments.find((segment) => segment.IsFinish);
    }
    // Find the segment for the shooting station boundry
    if (role.IsShootingStation) {
        segment = segments.find((segment) => segment.IsShooting && segment.ShootingNumber === currentShootingNumber);
    }

    // If we are not in pre-start, and not on a boundry, we just want to get into the non-boundry segment for the current shootingnumber
    if (!segment) {
        segment = segments.find(
            (segment) =>
                segment.ShootingNumber === currentShootingNumber &&
                !segment.IsPreStart &&
                !segment.IsStart &&
                !segment.IsStartControl &&
                !segment.IsFinish &&
                !segment.IsShooting,
        );
    }

    // If the segment doesnt exist, then we just add it
    if (!segment) {
        segment = {
            IsStart: role.Start,
            IsPreStart: roleIndex < startIndex,
            IsStartControl: role.StartControl,
            IsFinish: role.Finish,
            IsShooting: role.IsShootingStation,
            ShootingNumber: currentShootingNumber,
            StationRoles: [],
        };
        segments.push(segment);

        // The order here might be wrong if we are handling a race without prestart, before a race with prestart (or with/without start or ...)
        // So we make sure we sort the segments every time we add something
        segments.sort((a, b) => {
            // Make sure we get the order prestart, start, startcontrol, shooting1, shooting2, finish
            const order = (segment: RaceSegment) => {
                if (segment.IsPreStart) return 0;
                if (segment.IsStart) return 1;
                if (segment.IsStartControl) return 2;
                if (segment.IsFinish) return 1000;
                return 3 + (segment.ShootingNumber ?? 0);
            };
            // We have to special handle shooting since we sort by shootingnumber,
            // and we want the actual shooting to be at the border
            if (a.IsShooting && !b.IsShooting && a.ShootingNumber === b.ShootingNumber) return -1;
            if (!a.IsShooting && b.IsShooting && a.ShootingNumber === b.ShootingNumber) return 1;
            return order(a) - order(b);
        });
    }
    return segment;
};
